import { cn } from '@nexo/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../../lib/i18n';

/**
 * Single-series daily bar chart (one hue, no legend: the title names the series). Thin bars
 * with rounded data-ends on a shared baseline, a 2px gap between bars, a hover tooltip and a
 * visually hidden table for screen readers.
 */
export function MiniBarChart({
  title,
  data,
}: {
  title: string;
  data: { date: string; value: number }[];
}) {
  const { t } = useTranslation();
  const dayLabel = new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'short' });
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => d.value));
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const active = hover !== null ? data[hover] : undefined;

  return (
    <figure className="rounded-xl border border-border p-4">
      <figcaption className="mb-3 flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-xs text-muted tabular-nums">
          {active
            ? `${dayLabel.format(new Date(active.date))}: ${active.value}`
            : t('{{count}} en 14 días', { count: total })}
        </span>
      </figcaption>
      <div
        className="relative flex h-28 items-end gap-[2px] border-b border-border"
        onMouseLeave={() => setHover(null)}
        aria-hidden
      >
        {data.map((d, i) => (
          <div
            key={d.date}
            className="flex h-full flex-1 items-end"
            onMouseEnter={() => setHover(i)}
          >
            <div
              className={cn(
                'w-full rounded-t-[4px] bg-accent transition-opacity',
                hover !== null && hover !== i && 'opacity-40',
              )}
              style={{ height: d.value === 0 ? 0 : `${Math.max(4, (d.value / max) * 100)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-subtle" aria-hidden>
        <span>{data[0] ? dayLabel.format(new Date(data[0].date)) : ''}</span>
        <span>{t('Hoy')}</span>
      </div>
      <table className="sr-only">
        <caption>{title}</caption>
        <tbody>
          {data.map((d) => (
            <tr key={d.date}>
              <th scope="row">{d.date}</th>
              <td>{d.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
